import React, { useState } from "react";
import { Icon } from "@iconify/react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace, WORKSPACES } from "../../contexts/Workspacecontext";
import ProfileIcon from "../ProfileIcon/ProfileIcon";
import "./Navbar.scss";
import DefaultLogo from "../../Images/CompanyLogo.png";
import DefaultProfileIcon from "../../Images/user-1.jpg";
import hrlogo from "../../Images/HrLabs_Logo.jpeg";

// ─────────────────────────────────────────────────────────────────────────────
// Quick-access link definitions, keyed by workspace → role
// Each role array lists { name, route } in priority order.
// The lookup falls back to ROLE_EMP if the exact role isn't found.
// ─────────────────────────────────────────────────────────────────────────────
const QUICK_ACCESS_MAP = {
  [WORKSPACES.COMPANY]: {
    "ROLE_RECRUITER-HR": [
      { name: "All Vacancies", route: "/recruitment/vacancies/all" },
      { name: "All Job Posts", route: "/recruitment/job-posts/all" },
      { name: "All Candidates", route: "/recruitment/candidates/all" },
      { name: "Interview Pipeline", route: "/recruitment/interviews/pipeline" },
      { name: "Hiring Flows", route: "/recruitment/hiring-flow/all" },
      { name: "Job Drives", route: "/recruitment/job-drives/all" },
      { name: "Connections", route: "/recruitment/connections/candidates" },
    ],
  },

  [WORKSPACES.CONSULTANCY]: {
    "ROLE_CONSULTANT-ADMIN": [
      { name: "All Job Posts", route: "/consultant/job-posts/all" },
      { name: "All Candidates", route: "/consultant/candidates/all" },
      { name: "Job Drives", route: "/consultant/job-drives/all" },
      { name: "Connections", route: "/consultant/connections" },
      { name: "Share Candidates", route: "/consultant/share-candidates" },
      { name: "My Subscriptions", route: "/subscriptions" },
    ],

    ROLE_CONSULTANT: [
      { name: "All Job Posts", route: "/consultant/job-posts/all" },
      { name: "All Candidates", route: "/consultant/candidates/all" },
      { name: "Job Drives", route: "/consultant/job-drives/all" },
      { name: "Connections", route: "/consultant/connections" },
      { name: "Share Candidates", route: "/consultant/share-candidates" },
    ],
  },
};
// ─────────────────────────────────────────────────────────────────────────────
// Search menu — what the global search bar can navigate to, per role+workspace
// ─────────────────────────────────────────────────────────────────────────────
const SEARCH_MENU_MAP = {
  [WORKSPACES.COMPANY]: {
    "ROLE_RECRUITER-HR": [
      { name: "Dashboard", route: "/dashboard" },
      { name: "All Vacancies", route: "/recruitment/vacancies/all" },
      { name: "Create Vacancy", route: "/recruitment/vacancies/create" },
      { name: "Pending Approvals", route: "/recruitment/vacancies/approvals" },
      { name: "Approved Vacancies", route: "/recruitment/vacancies/approved" },
      { name: "All Job Posts", route: "/recruitment/job-posts/all" },
      { name: "All Candidates", route: "/recruitment/candidates/all" },
      { name: "Applications", route: "/recruitment/candidates/applied" },
      {
        name: "Candidate Requirement",
        route: "/recruitment/candidate-requirement",
      },
      { name: "Interview Pipeline", route: "/recruitment/interviews/pipeline" },
      { name: "Hiring Flows", route: "/recruitment/hiring-flow/all" },
      { name: "Job Drives", route: "/recruitment/job-drives/all" },
      { name: "Connections", route: "/recruitment/connections/candidates" },
      { name: "Share Requirement", route: "/recruitment/connections/share" },
      { name: "My Leave", route: "/ess/my-leave" },
      { name: "List of Holidays", route: "/ess/list-of-holidays" },
      { name: "Policies", route: "/ess/policies" },
    ],
  },
  [WORKSPACES.CONSULTANCY]: {
    "ROLE_CONSULTANT-ADMIN": [
      { name: "Dashboard", route: "/dashboard" },
      { name: "All Job Posts", route: "/consultant/job-posts/all" },
      { name: "All Candidates", route: "/consultant/candidates/all" },
      { name: "Job Drives", route: "/consultant/job-drives/all" },
      { name: "Connections", route: "/consultant/connections" },
      { name: "Share Candidates", route: "/consultant/share-candidates" },
      {
        name: "Candidate Requirement",
        route: "/consultant/candidate-requirement",
      },
      { name: "My Subscriptions", route: "/subscriptions" },
    ],
    ROLE_CONSULTANT: [
      { name: "Dashboard", route: "/dashboard" },
      { name: "All Job Posts", route: "/consultant/job-posts/all" },
      { name: "All Candidates", route: "/consultant/candidates/all" },
      { name: "Job Drives", route: "/consultant/job-drives/all" },
      { name: "Connections", route: "/consultant/connections" },
      { name: "Share Candidates", route: "/consultant/share-candidates" },
      {
        name: "Candidate Requirement",
        route: "/consultant/candidate-requirement",
      },
    ],
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper: pick the right menu list for the current user
// ─────────────────────────────────────────────────────────────────────────────
const resolveMenuItems = (map, workspace, authorities) => {
  const wsMap = map[workspace] ?? {};

  // Priority order: most-privileged role first
  const ROLE_PRIORITY = [
    "ROLE_ADMIN",
    "ROLE_CONSULTANT-ADMIN",
    "ROLE_RECRUITER-HR",
    "ROLE_CONSULTANT",
    "ROLE_EMP",
  ];

  for (const role of ROLE_PRIORITY) {
    if (authorities.includes(role) && wsMap[role]) {
      return wsMap[role];
    }
  }

  // fallback: first available bucket in this workspace
  return Object.values(wsMap)[0] ?? [];
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const Navbar = ({ onNotificationReceived }) => {
  const { user, logout } = useAuth();
  const { workspace } = useWorkspace();
  const navigate = useNavigate();

  const [menuState, setMenuState] = useState({
    isSearchExpanded: false,
    openDropdown: null,
    quickAccessOpen: false,
  });
  const [searchQuery, setSearchQuery] = useState("");

  // ── Derived data ──────────────────────────────────────────────────────────
  const logoImageSrc =
    user?.logo && user?.logoType
      ? `data:${user.logoType};base64,${user.logo}`
      : DefaultLogo;
  const profileImageSrc =
    user?.profileImage && user?.profileImageType
      ? `data:${user.profileImageType};base64,${user.profileImage}`
      : DefaultProfileIcon;
  const companyName = user?.companyName || "HRLabs";
  const employeeName = user?.employeeName || "Employee";
  const role = user?.role || "No Role Assigned";
  const authorities = user?.authorities || [];

  const isCompany = workspace === WORKSPACES.COMPANY;
  const wsLabel = isCompany ? "🏢 Company" : "🤝 Consultancy";
  const wsTitle = isCompany
    ? "Switch to Consultancy workspace"
    : "Switch to Company workspace";

  // ── Role-aware menus ──────────────────────────────────────────────────────
  const allSearchItems = resolveMenuItems(
    SEARCH_MENU_MAP,
    workspace,
    authorities,
  );
  const quickAccessItems = resolveMenuItems(
    QUICK_ACCESS_MAP,
    workspace,
    authorities,
  );

  const filteredSearchItems = searchQuery.trim()
    ? allSearchItems.filter((item) =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : [];

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleLogoError = (e) => {
    if (e.currentTarget.src.includes("CompanyLogo")) {
      e.currentTarget.onerror = null;
      e.currentTarget.src = { hrlogo };
    }
  };
  const set = (patch) => setMenuState((p) => ({ ...p, ...patch }));

  const formatRole = (r) =>
    r
      .replace("ROLE_", "")
      .toLowerCase()
      .split(/[_-]/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  if (!user) return null;

  return (
    <nav className="navbar">
      <div className="navbar-left">
        <img
          src={logoImageSrc}
          alt="Company Logo"
          className="company-logo"
          onError={handleLogoError}
        />
        <span className="company-name">{companyName}</span>
      </div>

      <div className="navbar-right">
        {/* ── Search ── */}
        <div
          className={`search-container ${menuState.isSearchExpanded ? "expanded" : ""}`}
          onMouseEnter={() => set({ isSearchExpanded: true })}
          onMouseLeave={() => set({ isSearchExpanded: false })}
        >
          <input
            type="text"
            placeholder="Search..."
            id="search"
            className={`search-input ${menuState.isSearchExpanded ? "expanded" : ""}`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Icon
            icon="ion:search-outline"
            className="search-icon"
            aria-label="Toggle Search"
          />
          {menuState.isSearchExpanded && filteredSearchItems.length > 0 && (
            <div className="filtered-menu">
              {filteredSearchItems.map((item) => (
                <Link
                  key={item.route}
                  to={item.route}
                  className="filtered-item"
                  onClick={() => {
                    setSearchQuery("");
                    set({ isSearchExpanded: false });
                  }}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* ── Workspace switch ── */}
        {workspace && (
          <button
            className="navbar-ws-btn"
            onClick={() => navigate("/select-workspace")}
            title={wsTitle}
          >
            {wsLabel}
          </button>
        )}

        {/* ── Quick Access ── */}
        <div
          className="quick-access-container"
          onMouseEnter={() => set({ quickAccessOpen: true })}
          onMouseLeave={() => set({ quickAccessOpen: false })}
        >
          <button className="quick-access-button" aria-label="Quick Access">
            <Icon icon="solar:list-arrow-down-broken" />
          </button>
          {menuState.quickAccessOpen && quickAccessItems.length > 0 && (
            <div className="quick-access-menu">
              {quickAccessItems.map((item) => (
                <Link key={item.route} to={item.route}>
                  {item.name}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* ── Profile ── */}
        <div
          className="profile-menu-container"
          onMouseEnter={() => set({ openDropdown: "profile" })}
          onMouseLeave={() => set({ openDropdown: null })}
        >
          <button className="profile-button" aria-label="Profile Menu">
            <ProfileIcon
              size="small"
              src={profileImageSrc}
              alt="Profile"
              className="profile-avatar"
            />
            <Icon icon="mdi:chevron-down" />
          </button>
          {menuState.openDropdown === "profile" && (
            <div className="profile-menu">
              <div className="profile-info">
                <ProfileIcon
                  size="medium"
                  src={profileImageSrc}
                  alt="Profile"
                />
                <div className="profile-details">
                  <div className="profile-name">{employeeName}</div>
                  <div className="profile-role">
                    <span className="role">{formatRole(role)}</span>
                  </div>
                </div>
              </div>
              <div className="profile-options">
                <Link to="/profile">Profile</Link>
                {/* Subscriptions: Admin in company OR Consultant-Admin in consultancy */}
                {(authorities.includes("ROLE_ADMIN") ||
                  authorities.includes("ROLE_CONSULTANT-ADMIN")) && (
                  <Link to="/subscriptions">My Subscriptions</Link>
                )}
                <Link to="/account-settings">Account Settings</Link>
                <a
                  href="#logout"
                  className="logout-a"
                  onClick={(e) => {
                    e.preventDefault();
                    logout();
                  }}
                >
                  Logout
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
